"use client";

import { GoogleIcon } from "@/components/google-icon";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackEvent } from "@/components/vident-provider";
import { authClient } from "@/lib/auth-client";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export default function SignUpPage() {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const router = useRouter();

	useEffect(() => {
		trackEvent("signup_page_viewed");
	}, []);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError("");
		setLoading(true);

		const { error: authError } = await authClient.signUp.email({
			name,
			email,
			password,
		});

		if (authError) {
			setError(authError.message ?? "Sign up failed");
			setLoading(false);
			return;
		}

		router.push("/setup");
	}

	async function handleGoogleSignUp() {
		await authClient.signIn.social({
			provider: "google",
			callbackURL: `${process.env.NEXT_PUBLIC_SITE_URL || "https://unranked.io"}/setup`,
		});
	}

	return (
		<div className="flex min-h-screen flex-col items-center justify-center px-6">
			<div className="mb-8 flex items-center gap-2">
				<div className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-500">
					<span className="font-sans text-lg font-semibold text-white">u</span>
				</div>
				<span className="font-mono text-sm text-foreground/80">unranked</span>
			</div>

			<Card className="w-full max-w-sm border-border bg-card/50">
				<CardContent className="pt-6">
					<h1 className="mb-2 text-center font-sans text-2xl font-semibold text-foreground">
						Create account
					</h1>
					<p className="mb-6 text-center font-mono text-xs text-muted-foreground">
						See your keywords before you pay
					</p>

					<form onSubmit={handleSubmit} className="flex flex-col gap-4">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="name" className="font-mono text-xs text-muted-foreground">
								Name
							</Label>
							<Input
								id="name"
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="Your name"
								required
								className="h-10 font-mono text-sm"
							/>
						</div>

						<div className="flex flex-col gap-1.5">
							<Label htmlFor="email" className="font-mono text-xs text-muted-foreground">
								Email
							</Label>
							<Input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@example.com"
								required
								className="h-10 font-mono text-sm"
							/>
						</div>

						<div className="flex flex-col gap-1.5">
							<Label htmlFor="password" className="font-mono text-xs text-muted-foreground">
								Password
							</Label>
							<Input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Min 8 characters"
								required
								minLength={8}
								className="h-10 font-mono text-sm"
							/>
						</div>

						{error && <p className="font-mono text-xs text-red-400">{error}</p>}

						<Button
							type="submit"
							disabled={loading}
							className="mt-2 h-10 bg-indigo-500 font-mono text-sm tracking-wide text-white hover:bg-indigo-400"
						>
							{loading ? "Creating account..." : "Sign up"}
						</Button>
					</form>

					<div className="my-6 flex items-center gap-3">
						<div className="h-px flex-1 bg-border" />
						<span className="font-mono text-xs text-muted-foreground">or</span>
						<div className="h-px flex-1 bg-border" />
					</div>

					<Button
						type="button"
						variant="outline"
						onClick={handleGoogleSignUp}
						className="h-10 w-full font-mono text-sm"
					>
						<GoogleIcon className="mr-2 h-4 w-4" />
						Continue with Google
					</Button>

					<p className="mt-6 text-center font-mono text-xs text-muted-foreground">
						Already have an account?{" "}
						<a href="/sign-in" className="text-indigo-400 hover:text-indigo-500">
							Sign in
						</a>
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
